import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  ArrowLeft,
  CalendarClock,
  CalendarDays,
  ChevronDown,
  ClipboardList,
  Clock3,
  MapPin,
  Plus,
  RefreshCcw,
  Save,
  Search,
  Trash2,
  UserPlus,
  UserRound,
  X,
} from 'lucide-react';
import { useApiClient } from '../lib/apiClient';
import { useI18n } from '../i18n/LanguageContext';
import InitialAvatar from './InitialAvatar';
import {
  type CoordinationEpisode,
  type CoordinationExternalUser,
  type CoordinationPlanningInternalUser,
  type CoordinationPlanningMedicalForm,
  type CoordinationPlanningOptions,
  type CoordinationTask,
  type CoordinationTaskCatalogEntry,
  type CoordinationTaskMetadata,
  type CoordinationWorkspaceResponse,
  formatCoordinationDate,
  formatCoordinationDay,
} from '../lib/careCoordination';

type PatientIdentity = {
  id: string;
  firstName: string;
  lastName: string;
  mrn?: string;
};

type ProviderDirectoryEntry = {
  id: string;
  userId?: string | null;
  firstName: string;
  lastName: string;
  title?: string | null;
  type: string;
  avatarUrl?: string | null;
  active: boolean;
  linkedUser?: {
    id: string;
    email: string;
    role: string;
  } | null;
};

type EpisodeDraft = {
  type: string;
  reason: string;
  riskLevel: CoordinationEpisode['riskLevel'] | '';
  status: CoordinationEpisode['status'] | '';
  expectedEndDate: string;
  coordinatorId: string;
};

type PlannerTaskDraft = {
  id: string;
  templateCode: string;
  category: string;
  title: string;
  description: string;
  priority: string;
  dueAt: string;
  assigneeType: 'internal' | 'external' | 'unassigned';
  internalUserId: string;
  externalUserId: string;
  recurrenceType: 'ONE_TIME' | 'DAILY' | 'WEEKLY' | 'BIWEEKLY' | 'MONTHLY' | 'CUSTOM_DAYS';
  recurrenceEvery: string;
  medicalFormIds: string[];
  automationMode: 'MANUAL' | 'ON_TASK_DATE' | 'DAYS_BEFORE' | 'DAYS_AFTER';
  automationOffsetDays: string;
};

type EpisodeMetrics = {
  taskCount: number;
  formTaskCount: number;
  nextDueAt: string | null;
};

type TaskEditorDraft = {
  title: string;
  description: string;
  priority: string;
  status: CoordinationTask['status'];
  dueAt: string;
};

const riskLevels: CoordinationEpisode['riskLevel'][] = ['LOW', 'MEDIUM', 'HIGH', 'CRITICAL'];
const episodeStatusOptions: CoordinationEpisode['status'][] = ['ACTIVE', 'ON_HOLD', 'CLOSED', 'CANCELLED'];
const taskStatusOptions: CoordinationTask['status'][] = [
  'NEW',
  'ASSIGNED',
  'IN_PROGRESS',
  'WAITING_EXTERNAL',
  'COMPLETED',
  'ESCALATED',
  'CANCELLED',
];
const recurrenceOptions: PlannerTaskDraft['recurrenceType'][] = ['ONE_TIME', 'DAILY', 'WEEKLY', 'BIWEEKLY', 'MONTHLY', 'CUSTOM_DAYS'];
const automationOptions: PlannerTaskDraft['automationMode'][] = ['MANUAL', 'ON_TASK_DATE', 'DAYS_BEFORE', 'DAYS_AFTER'];
const plannerFieldClass =
  'w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ink/10 bg-white';

const coordinationItOverrides: Record<string, string> = {
  'Care Episodes': 'Episodi di cura',
  Provider: 'Provider',
  Custom: 'Personalizzato',
  'One time': 'Una tantum',
  Daily: 'Giornaliero',
  Weekly: 'Settimanale',
  Biweekly: 'Bisettimanale',
  Monthly: 'Mensile',
  Manual: 'Manuale',
  'Send on task date': "Invia alla data dell'attività",
  'Internal / named care team': 'Interno / team di cura nominato',
  'Clinic member': 'Membro della clinica',
  'Internal assignee': 'Assegnatario interno',
  'External user': 'Utente esterno',
  'External assignee': 'Assegnatario esterno',
  Unassigned: 'Non assegnato',
  Internal: 'Interno',
  External: 'Esterno',
  Low: 'Basso',
  Medium: 'Medio',
  High: 'Alto',
  Critical: 'Critico',
  Active: 'Attivo',
  'On Hold': 'In attesa',
  Closed: 'Chiuso',
  Cancelled: 'Annullato',
  New: 'Nuovo',
  Assigned: 'Assegnato',
  'In Progress': 'In corso',
  'Waiting External': 'In attesa esterno',
  Completed: 'Completato',
  Escalated: 'Escalato',
  'Episode updated.': 'Episodio aggiornato.',
  'Add at least one task before saving the care plan.': 'Aggiungi almeno un’attività prima di salvare il piano assistenziale.',
  'Episode type and reason are required.': "Il tipo di episodio e il motivo sono obbligatori.",
  'Care episode and plan created.': 'Episodio assistenziale e piano creati.',
  'Task title is required.': "Il titolo dell'attività è obbligatorio.",
  'Task updated.': 'Attività aggiornata.',
  'No episode selected for task creation.': "Nessun episodio selezionato per creare l'attività.",
  'Open and past care periods': 'Periodi assistenziali aperti e passati',
  'Across all episodes': 'In tutti gli episodi',
  'No touchpoint scheduled': 'Nessun contatto pianificato',
  'Plan Tasks': 'Attività pianificate',
  'Tasks with Forms': 'Attività con moduli',
  'Manage care-plan rows here, and add new tasks from the table toolbar.':
    'Gestisci qui le righe del piano assistenziale e aggiungi nuove attività dalla barra della tabella.',
  'Start from an episode, then build the recurring task plan, assignees, and form automation in one flow.':
    'Parti da un episodio, poi costruisci in un unico flusso il piano attività ricorrenti, gli assegnatari e l’automazione dei moduli.',
  'New Care Episode': 'Nuovo episodio assistenziale',
  Episodes: 'Episodi',
  'Open tasks': 'Attività aperte',
  'Active risk': 'Rischio attivo',
  Episode: 'Episodio',
  'No episodes created yet.': 'Nessun episodio creato finora.',
  'Back to Episodes': 'Torna agli episodi',
  'Care Episode': 'Episodio assistenziale',
  'Select an episode from the list.': "Seleziona un episodio dall'elenco.",
  'Edit Episode': 'Modifica episodio',
  'Add Tasks': 'Aggiungi attività',
  'Episode Summary': "Riepilogo episodio",
  Type: 'Tipo',
  Reason: 'Motivo',
  Risk: 'Rischio',
  Status: 'Stato',
  Coordinator: 'Coordinatore',
  'Expected End': 'Fine prevista',
  'Plan Signals': 'Segnali del piano',
  Tasks: 'Attività',
  'With Forms': 'Con moduli',
  Recurring: 'Ricorrenti',
  Automation: 'Automazione',
  'Next touchpoint': 'Prossimo contatto',
  'Last contact': 'Ultimo contatto',
  'Overdue tasks': 'Attività scadute',
  'Care Plan Task Table': 'Tabella attività del piano assistenziale',
  'Select a row to open the task editor.': "Seleziona una riga per aprire l'editor attività.",
  Task: 'Attività',
  Assignee: 'Assegnatario',
  Recurrence: 'Ricorrenza',
  'Medical Forms': 'Moduli medici',
  'Send Rule': 'Regola di invio',
  'Next Due': 'Prossima scadenza',
  'Care task': 'Attività assistenziale',
  'No tasks planned for this episode yet.': 'Nessuna attività pianificata per questo episodio.',
  Assignments: 'Assegnazioni',
  'No assignees registered on this episode yet.': 'Nessun assegnatario registrato per questo episodio.',
  Cancel: 'Annulla',
  'Save Episode': 'Salva episodio',
  'Add Episode Tasks': "Aggiungi attività all'episodio",
  'Create Care Plan': 'Crea piano assistenziale',
  'Episode Details': "Dettagli dell'episodio",
  'Set the episode type, coordinator, risk, and timing using the same workflow style as appointment details.':
    "Imposta tipo episodio, coordinatore, rischio e tempistiche con lo stesso flusso dei dettagli appuntamento.",
  'Care Plan Tasks': 'Attività del piano assistenziale',
  'No tasks added yet': 'Nessuna attività aggiunta',
  'Add New Task': 'Aggiungi nuova attività',
  'Episode Type': 'Tipo di episodio',
  'Episode type': 'Tipo di episodio',
  'Risk Level': 'Livello di rischio',
  'Select risk level': 'Seleziona livello di rischio',
  'Select coordinator': 'Seleziona coordinatore',
  'Search providers by name, type, or email': 'Cerca provider per nome, tipo o email',
  'Select status': 'Seleziona stato',
  'Expected End Date': 'Data fine prevista',
  'Title / Reason': 'Titolo / motivo',
  'Clinical reason or episode notes': 'Motivo clinico o note episodio',
  'Edit Task': 'Modifica attività',
  'Cancel Task': 'Annulla attività',
  Close: 'Chiudi',
  'Save Task': 'Salva attività',
  'Task Summary': "Riepilogo dell'attività",
  'Review assignment and automation details for this care plan row.':
    "Rivedi dettagli di assegnazione e automazione per questa riga del piano assistenziale.",
  Category: 'Categoria',
  'Template Code': 'Codice template',
  'Current Due': 'Scadenza attuale',
  'Current Status': 'Stato attuale',
  'Editable Fields': 'Campi modificabili',
  'Update task details here instead of editing directly in the table.':
    "Aggiorna qui i dettagli dell'attività invece di modificarli direttamente nella tabella.",
  'Task Title': "Titolo dell'attività",
  Priority: 'Priorità',
  Instructions: 'Istruzioni',
  'Task instructions': "Istruzioni dell'attività",
  'Select internal assignee': 'Seleziona assegnatario interno',
  'Search team members by name, role, or email': 'Cerca membri del team per nome, ruolo o email',
  'Search by name, role, or email...': 'Cerca per nome, ruolo o email...',
  'No internal users found.': 'Nessun utente interno trovato.',
  Assignment: 'Assegnazione',
  'Due Date': 'Data di scadenza',
  Actions: 'Azioni',
  'Task title': "Titolo dell'attività",
  'Remove task': 'Rimuovi attività',
  'Add at least one task to build the care plan.': 'Aggiungi almeno un’attività per costruire il piano assistenziale.',
  All: 'Tutti',
  'Search task library': 'Cerca nella libreria attività',
  'Add custom task': 'Aggiungi attività personalizzata',
  Add: 'Aggiungi',
  'No tasks match this search.': 'Nessuna attività corrisponde a questa ricerca.',
};

function createCoordinationTranslator(language: string, tr: (value: string) => string) {
  if (language !== 'it') return tr;
  return (phrase: string) => {
    const normalized = phrase.trim();
    if (!normalized) return phrase;
    const globalTranslation = tr(normalized);
    if (globalTranslation !== normalized) return phrase.replace(normalized, globalTranslation);
    const local = coordinationItOverrides[normalized];
    return local ? phrase.replace(normalized, local) : phrase;
  };
}

function translateCoordinationRecurrence(label: string, tc: (value: string) => string) {
  const everyMatch = /^Every (\d+) days$/.exec(label);
  if (everyMatch) return `Ogni ${everyMatch[1]} giorni`;
  return tc(label);
}

function translateCoordinationAutomation(label: string, tc: (value: string) => string) {
  const beforeMatch = /^Send (\d+) day\(s\) before$/.exec(label);
  if (beforeMatch) return `Invia ${beforeMatch[1]} giorno/i prima`;
  const afterMatch = /^Send (\d+) day\(s\) after$/.exec(label);
  if (afterMatch) return `Invia ${afterMatch[1]} giorno/i dopo`;
  return tc(label);
}

function createLocalId(prefix: string) {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return `${prefix}-${crypto.randomUUID().slice(0, 8)}`;
  }
  return `${prefix}-${Math.random().toString(36).slice(2, 10)}`;
}

function toDateTimeInput(value?: string | null) {
  if (!value) return '';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '';
  const offsetMs = parsed.getTimezoneOffset() * 60 * 1000;
  return new Date(parsed.getTime() - offsetMs).toISOString().slice(0, 16);
}

function toDateInput(value?: string | null) {
  if (!value) return '';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '';
  return parsed.toISOString().slice(0, 10);
}

function toIsoOrUndefined(value: string) {
  if (!value) return undefined;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return undefined;
  return parsed.toISOString();
}

function toIsoOrNull(value: string) {
  const iso = toIsoOrUndefined(value);
  return iso ?? null;
}

function toDateIsoOrUndefined(value: string) {
  if (!value) return undefined;
  const parsed = new Date(`${value}T00:00:00.000Z`);
  if (Number.isNaN(parsed.getTime())) return undefined;
  return parsed.toISOString();
}

function toDateIsoOrNull(value: string) {
  const iso = toDateIsoOrUndefined(value);
  return iso ?? null;
}

function badgeClass(value: string) {
  const upper = value.toUpperCase();
  if (upper.includes('CRITICAL') || upper.includes('ESCALATED') || upper.includes('CANCELLED')) return 'bg-rose-100 text-rose-700';
  if (upper.includes('HIGH') || upper.includes('WAITING') || upper.includes('ON_HOLD')) return 'bg-amber-100 text-amber-800';
  if (upper.includes('COMPLETED') || upper.includes('CLOSED') || upper.includes('ACTIVE')) return 'bg-emerald-100 text-emerald-700';
  return 'bg-slate-100 text-slate-700';
}

function statusDotClass(value: string) {
  const upper = value.toUpperCase();
  if (upper.includes('CRITICAL') || upper.includes('ESCALATED') || upper.includes('CANCELLED')) return 'bg-rose-500';
  if (upper.includes('HIGH') || upper.includes('WAITING') || upper.includes('ON_HOLD')) return 'bg-amber-500';
  if (upper.includes('COMPLETED') || upper.includes('CLOSED') || upper.includes('ACTIVE')) return 'bg-emerald-500';
  return 'bg-sky-500';
}

function formatEnumLabel(value: string) {
  return value
    .toLowerCase()
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function providerToPickerUser(provider: ProviderDirectoryEntry): CoordinationPlanningInternalUser | null {
  const coordinatorId = typeof provider.userId === 'string' ? provider.userId.trim() : '';
  if (!coordinatorId) return null;
  const firstName = provider.firstName?.trim() || 'Provider';
  const lastName = provider.lastName?.trim() || '';
  return {
    id: coordinatorId,
    name: `${firstName} ${lastName}`.trim(),
    firstName,
    lastName,
    title: provider.title ?? null,
    email: provider.linkedUser?.email ?? '',
    phone: null,
    role: provider.type?.trim() || provider.linkedUser?.role || 'Provider',
    avatarUrl: provider.avatarUrl ?? null,
  };
}

function episodeToDraft(episode: CoordinationEpisode): EpisodeDraft {
  return {
    type: episode.type,
    reason: episode.reason,
    riskLevel: episode.riskLevel,
    status: episode.status,
    expectedEndDate: toDateInput(episode.expectedEndDate),
    coordinatorId: episode.coordinatorId || '',
  };
}

function defaultEpisodeDraft(): EpisodeDraft {
  return {
    type: '',
    reason: '',
    riskLevel: '',
    status: '',
    expectedEndDate: '',
    coordinatorId: '',
  };
}

function createTaskDraftFromTemplate(template?: CoordinationTaskCatalogEntry): PlannerTaskDraft {
  return {
    id: createLocalId('care-task'),
    templateCode: template?.code || '',
    category: template?.category || 'Custom',
    title: template?.title || '',
    description: template?.description || '',
    priority: formatEnumLabel(template?.suggestedPriority || 'NORMAL'),
    dueAt: '',
    assigneeType: 'unassigned',
    internalUserId: '',
    externalUserId: '',
    recurrenceType: template?.defaultRecurrence || 'ONE_TIME',
    recurrenceEvery: template?.defaultRecurrence === 'CUSTOM_DAYS' ? '7' : '1',
    medicalFormIds: [],
    automationMode: 'MANUAL',
    automationOffsetDays: '0',
  };
}

function recurrenceLabel(metadata?: CoordinationTaskMetadata) {
  const recurrence = metadata?.recurrence;
  if (!recurrence) return 'One time';
  switch (recurrence.type) {
    case 'DAILY':
      return 'Daily';
    case 'WEEKLY':
      return 'Weekly';
    case 'BIWEEKLY':
      return 'Biweekly';
    case 'MONTHLY':
      return 'Monthly';
    case 'CUSTOM_DAYS':
      return `Every ${recurrence.every || 1} days`;
    default:
      return 'One time';
  }
}

function automationLabel(metadata?: CoordinationTaskMetadata) {
  const automation = metadata?.automation;
  if (!automation) return 'Manual';
  if (automation.mode === 'ON_TASK_DATE') return 'Send on task date';
  if (automation.mode === 'DAYS_BEFORE') return `Send ${automation.offsetDays || 0} day(s) before`;
  if (automation.mode === 'DAYS_AFTER') return `Send ${automation.offsetDays || 0} day(s) after`;
  return 'Manual';
}

function formNames(metadata?: CoordinationTaskMetadata) {
  if (!metadata?.medicalForms?.length) return '-';
  return metadata.medicalForms.map((item) => item.name).join(', ');
}

function initials(name: string) {
  return (
    name
      .split(' ')
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase() ?? '')
      .join('') || '?'
  );
}

function taskToEditorDraft(task: CoordinationTask): TaskEditorDraft {
  return {
    title: task.title,
    description: task.description || '',
    priority: task.priority || '',
    status: task.status,
    dueAt: toDateTimeInput(task.dueAt),
  };
}

export function PatientCoordinationModule({ patient }: { patient: PatientIdentity }) {
  const { fetchJson } = useApiClient();
  const { language, tr } = useI18n();
  const tc = createCoordinationTranslator(language, tr);

  const [workspace, setWorkspace] = useState<CoordinationWorkspaceResponse | null>(null);
  const [options, setOptions] = useState<CoordinationPlanningOptions | null>(null);
  const [coordinatorProviders, setCoordinatorProviders] = useState<CoordinationPlanningInternalUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [banner, setBanner] = useState<string | null>(null);

  const [view, setView] = useState<'list' | 'episode'>('list');
  const [selectedEpisodeId, setSelectedEpisodeId] = useState<string | null>(null);
  const [plannerOpen, setPlannerOpen] = useState(false);
  const [plannerMode, setPlannerMode] = useState<'new' | 'append'>('new');
  const [episodeEditorOpen, setEpisodeEditorOpen] = useState(false);
  const [episodeDraft, setEpisodeDraft] = useState<EpisodeDraft>(defaultEpisodeDraft());
  const [plannerTasks, setPlannerTasks] = useState<PlannerTaskDraft[]>([]);
  const [taskEditorId, setTaskEditorId] = useState<string | null>(null);
  const [taskEditorDraft, setTaskEditorDraft] = useState<TaskEditorDraft | null>(null);

  async function loadWorkspace(silent = false) {
    try {
      if (!silent) setLoading(true);
      const response = await fetchJson<CoordinationWorkspaceResponse>(
        `/care-coordination/patients/${patient.id}/workspace?timelineLimit=120&includeClosed=true`,
      );
      setWorkspace(response);
      setError(null);
    } catch (err: any) {
      setError(err?.message || tr('Failed to load coordination workspace.'));
    } finally {
      if (!silent) setLoading(false);
    }
  }

  async function loadPlanningOptions() {
    const response = await fetchJson<CoordinationPlanningOptions>(`/care-coordination/patients/${patient.id}/planning-options`);
    setOptions(response);
  }

  async function loadCoordinatorProviders() {
    const response = await fetchJson<ProviderDirectoryEntry[]>('/providers');
    const normalized = (Array.isArray(response) ? response : [])
      .filter((provider) => provider.active !== false)
      .map(providerToPickerUser)
      .filter((provider): provider is CoordinationPlanningInternalUser => Boolean(provider));
    setCoordinatorProviders(normalized);
  }

  async function loadAll() {
    setLoading(true);
    setError(null);
    try {
      await Promise.all([loadWorkspace(true), loadPlanningOptions(), loadCoordinatorProviders()]);
    } catch (err: any) {
      setError(err?.message || tr('Failed to load care planning data.'));
    } finally {
      setLoading(false);
    }
  }

  async function sendJson<T>(path: string, init: { method: 'POST' | 'PATCH'; body: unknown }) {
    return fetchJson<T>(path, {
      method: init.method,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(init.body),
    });
  }

  useEffect(() => {
    void loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patient.id]);

  useEffect(() => {
    if (!plannerOpen && !episodeEditorOpen && !taskEditorDraft) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setPlannerOpen(false);
        setEpisodeEditorOpen(false);
        setTaskEditorId(null);
        setTaskEditorDraft(null);
      }
    };
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [episodeEditorOpen, plannerOpen, taskEditorDraft]);

  const selectedEpisode = useMemo(
    () => (selectedEpisodeId ? workspace?.episodes.find((item) => item.id === selectedEpisodeId) ?? null : null),
    [selectedEpisodeId, workspace],
  );

  const episodeTasks = useMemo(
    () => (selectedEpisodeId ? workspace?.tasks.filter((task) => task.episodeId === selectedEpisodeId) ?? [] : []),
    [selectedEpisodeId, workspace],
  );
  const selectedTaskForEdit = useMemo(
    () => (taskEditorId ? workspace?.tasks.find((task) => task.id === taskEditorId) ?? null : null),
    [taskEditorId, workspace],
  );

  const episodeTeam = useMemo(
    () =>
      selectedEpisodeId
        ? workspace?.careTeam.filter((member) => member.episodeId === selectedEpisodeId || member.episodeId === null) ?? []
        : [],
    [selectedEpisodeId, workspace],
  );

  const episodeMetrics = useMemo(() => {
    const map = new Map<string, EpisodeMetrics>();
    for (const episode of workspace?.episodes || []) {
      const tasks = (workspace?.tasks || []).filter((task) => task.episodeId === episode.id);
      const datedTasks = tasks
        .filter((task) => Boolean(task.dueAt))
        .sort((a, b) => Date.parse(a.dueAt || '') - Date.parse(b.dueAt || ''));
      map.set(episode.id, {
        taskCount: tasks.length,
        formTaskCount: tasks.filter((task) => (task.metadata?.medicalForms || []).length > 0).length,
        nextDueAt: datedTasks[0]?.dueAt || null,
      });
    }
    return map;
  }, [workspace]);

  const internalUsers = options?.internalUsers || [];
  const externalUsers = options?.externalUsers || [];
  const medicalForms = options?.medicalForms || [];
  const taskCatalog = options?.taskCatalog || [];
  const coordinatorUsers = useMemo(() => {
    const map = new Map(coordinatorProviders.map((item) => [item.id, item]));
    if (episodeDraft.coordinatorId && !map.has(episodeDraft.coordinatorId)) {
      const legacyCoordinator = internalUsers.find((item) => item.id === episodeDraft.coordinatorId);
      if (legacyCoordinator) map.set(legacyCoordinator.id, legacyCoordinator);
    }
    return Array.from(map.values());
  }, [coordinatorProviders, episodeDraft.coordinatorId, internalUsers]);

  const assignmentDirectory = useMemo(() => {
    const teamItems = episodeTeam.map((member) => ({
      id: `team:${member.id}`,
      name: member.actorName,
      role: member.role,
      type: member.actorType === 'PERSON' ? 'Internal / named care team' : member.actorType,
      contact: member.contact || '-',
    }));

    const taskDerived = episodeTasks
      .map((task) => {
        const assignee = task.metadata?.assignee;
        if (task.owner?.name) {
          const user = internalUsers.find((item) => item.id === task.ownerId);
          return {
            id: `task-owner:${task.id}`,
            name: task.owner.name,
            role: user?.role || tc('Clinic member'),
            type: tc('Internal assignee'),
            contact: user?.email || user?.phone || '-',
          };
        }
        if (assignee?.type === 'external') {
          const external = externalUsers.find((item) => item.id === assignee.externalUserId);
          return {
            id: `task-external:${task.id}`,
            name: external?.name || assignee.name || tc('External user'),
            role: external?.company || tc('External assignee'),
            type: tc('External assignee'),
            contact: external?.email || external?.phone || assignee.email || assignee.phone || '-',
          };
        }
        return null;
      })
      .filter(Boolean) as Array<{ id: string; name: string; role: string; type: string; contact: string }>;

    const dedupe = new Map<string, { id: string; name: string; role: string; type: string; contact: string }>();
    [...teamItems, ...taskDerived].forEach((item) => {
      const key = `${item.name}:${item.role}:${item.type}`;
      if (!dedupe.has(key)) dedupe.set(key, item);
    });
    return Array.from(dedupe.values());
  }, [episodeTasks, episodeTeam, externalUsers, internalUsers]);

  function assigneeLabel(task: CoordinationTask) {
    if (task.owner?.name) return task.owner.name;
    const assignee = task.metadata?.assignee;
    if (!assignee) return tc('Unassigned');
    if (assignee.type === 'internal' && assignee.internalUserId) {
      return internalUsers.find((item) => item.id === assignee.internalUserId)?.name || assignee.name || tc('Internal');
    }
    if (assignee.type === 'external' && assignee.externalUserId) {
      return externalUsers.find((item) => item.id === assignee.externalUserId)?.name || assignee.name || tc('External');
    }
    return tc('Unassigned');
  }

  function openEpisode(episode: CoordinationEpisode) {
    setSelectedEpisodeId(episode.id);
    setEpisodeDraft(episodeToDraft(episode));
    setView('episode');
    setError(null);
    setBanner(null);
  }

  function openNewPlanner() {
    setPlannerMode('new');
    setEpisodeDraft(defaultEpisodeDraft());
    setPlannerTasks([createTaskDraftFromTemplate()]);
    setPlannerOpen(true);
    setTaskEditorId(null);
    setTaskEditorDraft(null);
    setError(null);
    setBanner(null);
  }

  function openAppendPlanner() {
    setPlannerMode('append');
    if (selectedEpisode) {
      setEpisodeDraft(episodeToDraft(selectedEpisode));
    }
    setPlannerTasks([createTaskDraftFromTemplate()]);
    setPlannerOpen(true);
    setTaskEditorId(null);
    setTaskEditorDraft(null);
    setError(null);
    setBanner(null);
  }

  function openEpisodeEditor() {
    if (!selectedEpisode) return;
    setEpisodeDraft(episodeToDraft(selectedEpisode));
    setEpisodeEditorOpen(true);
    setTaskEditorId(null);
    setTaskEditorDraft(null);
  }

  function openTaskEditor(task: CoordinationTask) {
    setTaskEditorId(task.id);
    setTaskEditorDraft(taskToEditorDraft(task));
    setError(null);
    setBanner(null);
  }

  function closeTaskEditor() {
    setTaskEditorId(null);
    setTaskEditorDraft(null);
  }

  function addCatalogTask(template?: CoordinationTaskCatalogEntry) {
    setPlannerTasks((prev) => [...prev, createTaskDraftFromTemplate(template)]);
  }

  function patchPlannerTask(taskId: string, patch: Partial<PlannerTaskDraft>) {
    setPlannerTasks((prev) => prev.map((task) => (task.id === taskId ? { ...task, ...patch } : task)));
  }

  function toggleTaskForm(taskId: string, formId: string) {
    setPlannerTasks((prev) =>
      prev.map((task) =>
        task.id !== taskId
          ? task
          : {
              ...task,
              medicalFormIds: task.medicalFormIds.includes(formId)
                ? task.medicalFormIds.filter((item) => item !== formId)
                : [...task.medicalFormIds, formId],
            },
      ),
    );
  }

  function removePlannerTask(taskId: string) {
    setPlannerTasks((prev) => prev.filter((task) => task.id !== taskId));
  }

  async function saveEpisodeDetails() {
    if (!selectedEpisodeId) return;
    setBusy(true);
    setError(null);
    setBanner(null);
    try {
      await sendJson<CoordinationEpisode>(`/care-coordination/patients/${patient.id}/episodes/${selectedEpisodeId}`, {
        method: 'PATCH',
        body: {
          type: episodeDraft.type,
          reason: episodeDraft.reason,
          riskLevel: episodeDraft.riskLevel || undefined,
          status: episodeDraft.status || undefined,
          coordinatorId: episodeDraft.coordinatorId || null,
          expectedEndDate: toDateIsoOrNull(episodeDraft.expectedEndDate),
        },
      });
      await loadWorkspace(true);
      setEpisodeEditorOpen(false);
      setBanner(tc('Episode updated.'));
    } catch (err: any) {
      setError(err?.message || tr('Unable to update episode.'));
    } finally {
      setBusy(false);
    }
  }

  function buildTaskMetadata(task: PlannerTaskDraft) {
    const internalUser = internalUsers.find((item) => item.id === task.internalUserId);
    const externalUser = externalUsers.find((item) => item.id === task.externalUserId);
    const forms = medicalForms
      .filter((form) => task.medicalFormIds.includes(form.id))
      .map((form) => ({
        templateId: form.id,
        name: form.name,
        code: form.code,
      }));

    const metadata: CoordinationTaskMetadata = {
      template: {
        code: task.templateCode || null,
        category: task.category || null,
      },
      recurrence: {
        type: task.recurrenceType,
        every: task.recurrenceType === 'CUSTOM_DAYS' ? Number(task.recurrenceEvery) || 1 : null,
        description:
          task.recurrenceType === 'CUSTOM_DAYS' ? `Every ${Number(task.recurrenceEvery) || 1} day(s)` : recurrenceLabel({
            recurrence: { type: task.recurrenceType },
          }),
      },
      automation: {
        mode: task.automationMode,
        offsetDays:
          task.automationMode === 'DAYS_BEFORE' || task.automationMode === 'DAYS_AFTER'
            ? Number(task.automationOffsetDays) || 0
            : 0,
        description:
          task.automationMode === 'ON_TASK_DATE'
            ? 'Send on task date'
            : task.automationMode === 'DAYS_BEFORE'
              ? `Send ${Number(task.automationOffsetDays) || 0} day(s) before due date`
              : task.automationMode === 'DAYS_AFTER'
                ? `Send ${Number(task.automationOffsetDays) || 0} day(s) after due date`
                : 'Manual send',
      },
      medicalForms: forms,
      assignee:
        task.assigneeType === 'internal'
          ? {
              type: 'internal',
              internalUserId: internalUser?.id || null,
              name: internalUser?.name || null,
              email: internalUser?.email || null,
              phone: internalUser?.phone || null,
            }
          : task.assigneeType === 'external'
            ? {
                type: 'external',
                externalUserId: externalUser?.id || null,
                name: externalUser?.name || null,
                email: externalUser?.email || null,
                phone: externalUser?.phone || null,
                company: externalUser?.company || null,
              }
            : {
                type: 'unassigned',
              },
    };

    return metadata;
  }

  async function savePlanner() {
    const validTasks = plannerTasks.filter((task) => task.title.trim());
    if (!validTasks.length) {
      setError(tc('Add at least one task before saving the care plan.'));
      return;
    }
    if (!episodeDraft.type.trim() || !episodeDraft.reason.trim()) {
      setError(tc('Episode type and reason are required.'));
      return;
    }

    setBusy(true);
    setError(null);
    setBanner(null);
    try {
      let targetEpisodeId = selectedEpisodeId;
      if (plannerMode === 'new') {
        const created = await sendJson<CoordinationEpisode>(`/care-coordination/patients/${patient.id}/episodes`, {
          method: 'POST',
          body: {
            type: episodeDraft.type,
            reason: episodeDraft.reason,
            riskLevel: episodeDraft.riskLevel || undefined,
            coordinatorId: episodeDraft.coordinatorId || undefined,
            expectedEndDate: toDateIsoOrUndefined(episodeDraft.expectedEndDate),
          },
        });
        targetEpisodeId = created.id;
        setSelectedEpisodeId(created.id);
      }
      if (!targetEpisodeId) throw new Error(tc('No episode selected for task creation.'));

      for (const task of validTasks) {
        const status: CoordinationTask['status'] =
          task.assigneeType === 'external' ? 'WAITING_EXTERNAL' : task.assigneeType === 'internal' ? 'ASSIGNED' : 'NEW';
        await sendJson(`/care-coordination/patients/${patient.id}/tasks`, {
          method: 'POST',
          body: {
            episodeId: targetEpisodeId,
            title: task.title.trim(),
            description: task.description.trim() || undefined,
            status,
            priority: task.priority.trim() || undefined,
            ownerId: task.assigneeType === 'internal' ? task.internalUserId || undefined : undefined,
            dueAt: toIsoOrUndefined(task.dueAt),
            causedBy: 'CARE_PLAN_BUILDER',
            metadata: buildTaskMetadata(task),
          },
        });
      }

      await loadWorkspace(true);
      setPlannerOpen(false);
      setPlannerTasks([createTaskDraftFromTemplate()]);
      setView('episode');
      setBanner(
        plannerMode === 'new'
          ? tc('Care episode and plan created.')
          : language === 'it'
            ? `${validTasks.length} attività aggiunte all'episodio.`
            : `${validTasks.length} task${validTasks.length === 1 ? '' : 's'} added to the episode.`,
      );
    } catch (err: any) {
      setError(err?.message || tr('Unable to save care plan.'));
    } finally {
      setBusy(false);
    }
  }

  async function patchTask(
    task: CoordinationTask,
    patch: Partial<Pick<CoordinationTask, 'title' | 'status' | 'priority' | 'description' | 'dueAt'>>,
  ) {
    setBusy(true);
    setError(null);
    try {
      await sendJson(`/care-coordination/patients/${patient.id}/tasks/${task.id}`, {
        method: 'PATCH',
        body: {
          title: patch.title === undefined ? undefined : patch.title.trim(),
          priority:
            patch.priority === undefined ? undefined : patch.priority.trim() ? patch.priority.trim() : null,
          description:
            patch.description === undefined
              ? undefined
              : patch.description.trim()
                ? patch.description.trim()
                : null,
          status: patch.status,
          dueAt: patch.dueAt === undefined ? undefined : patch.dueAt ? patch.dueAt : null,
        },
      });
      await loadWorkspace(true);
    } catch (err: any) {
      setError(err?.message || tr('Unable to update task.'));
    } finally {
      setBusy(false);
    }
  }

  async function cancelTask(task: CoordinationTask) {
    const confirmationMessage = language === 'it' ? `Annullare "${task.title}"?` : `Cancel "${task.title}"?`;
    if (!window.confirm(confirmationMessage)) return;
    await patchTask(task, { status: 'CANCELLED' });
  }

  async function saveTaskDetails() {
    if (!selectedTaskForEdit || !taskEditorDraft) return;
    if (!taskEditorDraft.title.trim()) {
      setError(tc('Task title is required.'));
      return;
    }
    await patchTask(selectedTaskForEdit, {
      title: taskEditorDraft.title,
      description: taskEditorDraft.description,
      priority: taskEditorDraft.priority,
      status: taskEditorDraft.status,
      dueAt: toIsoOrNull(taskEditorDraft.dueAt),
    });
    closeTaskEditor();
    setBanner(tc('Task updated.'));
  }

  if (loading && !workspace) {
    return (
      <div className="rounded-xl border border-slate-200 bg-white p-6 text-sm text-slate-500">
        {tr('Loading coordination workspace...')}
      </div>
    );
  }

  if (!workspace) {
    return (
      <div className="rounded-xl border border-rose-200 bg-rose-50 p-6 text-sm text-rose-700">
        {error || tr('Unable to load workspace.')}
      </div>
    );
  }

  if (view === 'list') {
    return (
      <div className="space-y-4">
        <div className="rounded-2xl border border-slate-200 bg-white p-4">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <p className="text-xs uppercase tracking-wide text-slate-500">{tr('Coordination')}</p>
              <h3 className="text-lg font-semibold text-slate-900">
                {tr('Care Episodes')} - {patient.firstName} {patient.lastName}
              </h3>
              <p className="text-sm text-slate-600">
                {tc('Start from an episode, then build the recurring task plan, assignees, and form automation in one flow.')}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button className="inline-flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm" onClick={() => void loadAll()} disabled={busy}>
                <RefreshCcw size={14} className={busy ? 'animate-spin' : ''} /> {tr('Refresh')}
              </button>
              <button className="inline-flex items-center gap-2 rounded-lg bg-ink px-3 py-2 text-sm text-white disabled:opacity-60" onClick={openNewPlanner} disabled={busy}>
                <Plus size={14} /> {tc('New Care Episode')}
              </button>
            </div>
          </div>
        </div>

        {banner ? <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700">{banner}</div> : null}
        {error ? <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">{error}</div> : null}

        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <MetricCard label={tc('Episodes')} value={String(workspace.episodes.length)} helper={tc('Open and past care periods')} icon={<ClipboardList size={14} />} />
          <MetricCard label={tc('Open tasks')} value={String(workspace.summary.openTasks)} helper={tc('Across all episodes')} icon={<CalendarClock size={14} />} />
          <MetricCard
            label={tc('Active risk')}
            value={tc(formatEnumLabel(workspace.summary.riskLevel))}
            helper={workspace.summary.nextTouchpoint ? `${tc('Next touchpoint')} ${formatCoordinationDate(workspace.summary.nextTouchpoint)}` : tc('No touchpoint scheduled')}
            icon={<UserRound size={14} />}
          />
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-3 py-2 font-medium">{tc('Episode')}</th>
                <th className="px-3 py-2 font-medium">{tc('Status')}</th>
                <th className="px-3 py-2 font-medium">{tc('Risk')}</th>
                <th className="px-3 py-2 font-medium">{tc('Plan Tasks')}</th>
                <th className="px-3 py-2 font-medium">{tc('Tasks with Forms')}</th>
                <th className="px-3 py-2 font-medium">{tc('Next Due')}</th>
                <th className="px-3 py-2 font-medium">{tc('Coordinator')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {workspace.episodes.map((episode) => {
                const metrics = episodeMetrics.get(episode.id) || { taskCount: 0, formTaskCount: 0, nextDueAt: null };
                return (
                  <tr key={episode.id} className={`cursor-pointer hover:bg-slate-50 ${episode.status === 'ACTIVE' ? 'bg-lime-50/30 hover:bg-lime-50/50' : ''}`} onClick={() => openEpisode(episode)}>
                    <td className="px-3 py-3">
                      <div className="font-medium text-slate-900">{episode.type}</div>
                      <div className="text-xs text-slate-500">{episode.reason}</div>
                    </td>
                    <td className="px-3 py-3"><span className={`rounded-full px-2 py-0.5 text-xs ${badgeClass(episode.status)}`}>{tc(formatEnumLabel(episode.status))}</span></td>
                    <td className="px-3 py-3"><span className={`rounded-full px-2 py-0.5 text-xs ${badgeClass(episode.riskLevel)}`}>{tc(formatEnumLabel(episode.riskLevel))}</span></td>
                    <td className="px-3 py-3 text-slate-700">{metrics.taskCount}</td>
                    <td className="px-3 py-3 text-slate-700">{metrics.formTaskCount}</td>
                    <td className="px-3 py-3 text-slate-700">{formatCoordinationDate(metrics.nextDueAt)}</td>
                    <td className="px-3 py-3 text-slate-700">{episode.coordinator?.name || '-'}</td>
                  </tr>
                );
              })}
              {!workspace.episodes.length ? (
                <tr>
                  <td colSpan={7} className="px-3 py-4 text-slate-500">{tc('No episodes created yet.')}</td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>

        <PlannerDialog
          open={plannerOpen}
          mode={plannerMode}
          busy={busy}
          episodeDraft={episodeDraft}
          setEpisodeDraft={setEpisodeDraft}
          plannerTasks={plannerTasks}
          taskCatalog={taskCatalog}
          internalUsers={internalUsers}
          coordinatorUsers={coordinatorUsers}
          externalUsers={externalUsers}
          medicalForms={medicalForms}
          onClose={() => setPlannerOpen(false)}
          onAddCatalogTask={addCatalogTask}
          onPatchTask={patchPlannerTask}
          onToggleTaskForm={toggleTaskForm}
          onRemoveTask={removePlannerTask}
          onSave={() => void savePlanner()}
        />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="rounded-2xl border border-slate-200 bg-white p-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <button className="inline-flex items-center gap-1 rounded-lg border border-slate-200 px-3 py-2 text-sm" onClick={() => { setView('list'); setSelectedEpisodeId(null); setPlannerOpen(false); setEpisodeEditorOpen(false); closeTaskEditor(); }}>
              <ArrowLeft size={14} /> {tc('Back to Episodes')}
            </button>
            <div>
              <p className="text-xs uppercase tracking-wide text-slate-500">{tc('Care Episode')}</p>
              <h3 className="text-lg font-semibold text-slate-900">
                {selectedEpisode?.type || tc('Episode')} {selectedEpisode ? `(${tc(formatEnumLabel(selectedEpisode.status))})` : ''}
              </h3>
              <p className="text-sm text-slate-500">{selectedEpisode?.reason || tc('Select an episode from the list.')}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button className="inline-flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm" onClick={() => void loadAll()} disabled={busy}>
              <RefreshCcw size={14} className={busy ? 'animate-spin' : ''} /> {tr('Refresh')}
            </button>
            <button className="inline-flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm" onClick={openEpisodeEditor} disabled={!selectedEpisode}>
              <Save size={14} /> {tc('Edit Episode')}
            </button>
            <button className="inline-flex items-center gap-2 rounded-lg bg-ink px-3 py-2 text-sm text-white disabled:opacity-60" onClick={openAppendPlanner} disabled={!selectedEpisodeId}>
              <Plus size={14} /> {tc('Add Tasks')}
            </button>
          </div>
        </div>
      </div>

      {banner ? <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700">{banner}</div> : null}
      {error ? <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">{error}</div> : null}

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
        <Section title={tc('Episode Summary')}>
          <InfoRows
            rows={[
              [tc('Type'), selectedEpisode?.type || '-'],
              [tc('Reason'), selectedEpisode?.reason || '-'],
              [tc('Risk'), selectedEpisode ? <span className={`rounded-full px-2 py-0.5 text-xs ${badgeClass(selectedEpisode.riskLevel)}`}>{tc(formatEnumLabel(selectedEpisode.riskLevel))}</span> : '-'],
              [tc('Status'), selectedEpisode ? <span className={`rounded-full px-2 py-0.5 text-xs ${badgeClass(selectedEpisode.status)}`}>{tc(formatEnumLabel(selectedEpisode.status))}</span> : '-'],
              [tc('Coordinator'), selectedEpisode?.coordinator?.name || '-'],
              [tc('Expected End'), formatCoordinationDay(selectedEpisode?.expectedEndDate)],
            ]}
          />
        </Section>

        <Section title={tc('Plan Signals')}>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <SignalCard label={tc('Tasks')} value={String(episodeTasks.length)} />
            <SignalCard label={tc('With Forms')} value={String(episodeTasks.filter((task) => (task.metadata?.medicalForms || []).length > 0).length)} />
            <SignalCard label={tc('Recurring')} value={String(episodeTasks.filter((task) => task.metadata?.recurrence?.type && task.metadata.recurrence.type !== 'ONE_TIME').length)} />
            <SignalCard label={tc('External')} value={String(episodeTasks.filter((task) => task.metadata?.assignee?.type === 'external').length)} />
          </div>
        </Section>

        <Section title={tc('Automation')}>
          <InfoRows
            rows={[
              [tc('Next touchpoint'), formatCoordinationDate(workspace.summary.nextTouchpoint)],
              [tc('Last contact'), formatCoordinationDate(workspace.summary.lastPatientContact)],
              [tc('Open tasks'), String(workspace.summary.openTasks)],
              [tc('Overdue tasks'), String(workspace.summary.overdueTasks)],
            ]}
          />
        </Section>
      </div>

      <Section title={tc('Care Plan Task Table')}>
        <div className="text-xs text-slate-500">{tc('Select a row to open the task editor.')}</div>
        <div className="overflow-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-3 py-2 font-medium">{tc('Task')}</th>
                <th className="px-3 py-2 font-medium">{tc('Assignee')}</th>
                <th className="px-3 py-2 font-medium">{tc('Recurrence')}</th>
                <th className="px-3 py-2 font-medium">{tc('Medical Forms')}</th>
                <th className="px-3 py-2 font-medium">{tc('Send Rule')}</th>
                <th className="px-3 py-2 font-medium">{tc('Status')}</th>
                <th className="px-3 py-2 font-medium">{tc('Next Due')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {episodeTasks.map((task) => (
                <tr
                  key={task.id}
                  role="button"
                  tabIndex={0}
                  className="cursor-pointer transition-colors hover:bg-slate-50 focus:outline-none focus-visible:bg-slate-50"
                  onClick={() => openTaskEditor(task)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      openTaskEditor(task);
                    }
                  }}
                >
                  <td className="px-3 py-3 align-top">
                    <div className="font-medium text-slate-900">{task.title}</div>
                    <div className="text-xs text-slate-500">
                      {task.metadata?.template?.category || tc('Care task')}
                      {task.metadata?.template?.code ? ` • ${task.metadata.template.code}` : ''}
                    </div>
                  </td>
                  <td className="px-3 py-3 align-top text-slate-700">{assigneeLabel(task)}</td>
                  <td className="px-3 py-3 align-top text-slate-700">{translateCoordinationRecurrence(recurrenceLabel(task.metadata), tc)}</td>
                  <td className="px-3 py-3 align-top text-slate-700">{formNames(task.metadata)}</td>
                  <td className="px-3 py-3 align-top text-slate-700">{translateCoordinationAutomation(automationLabel(task.metadata), tc)}</td>
                  <td className="px-3 py-3 align-top">
                    <span className={`rounded-full px-2 py-0.5 text-xs ${badgeClass(task.status)}`}>{tc(formatEnumLabel(task.status))}</span>
                  </td>
                  <td className="px-3 py-3 align-top text-slate-700">{formatCoordinationDate(task.dueAt)}</td>
                </tr>
              ))}
              {!episodeTasks.length ? (
                <tr>
                  <td colSpan={7} className="px-3 py-4 text-slate-500">{tc('No tasks planned for this episode yet.')}</td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </Section>

      <Section title={tc('Assignments')}>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
          {assignmentDirectory.map((item) => (
            <div key={item.id} className="rounded-xl border border-slate-200 bg-slate-50 p-3">
              <div className="flex items-center gap-3">
                <div className="flex h-9 w-9 items-center justify-center rounded-full bg-slate-900 text-xs font-semibold text-white">{initials(item.name)}</div>
                <div>
                  <div className="font-medium text-slate-900">{item.name}</div>
                  <div className="text-xs text-slate-500">{item.role}</div>
                </div>
              </div>
              <div className="mt-3 text-xs text-slate-600">
                <div>{item.type}</div>
                <div>{item.contact}</div>
              </div>
            </div>
          ))}
          {!assignmentDirectory.length ? <div className="text-sm text-slate-500">{tc('No assignees registered on this episode yet.')}</div> : null}
        </div>
      </Section>

      <PlannerDialog
        open={plannerOpen}
        mode={plannerMode}
        busy={busy}
        episodeDraft={episodeDraft}
        setEpisodeDraft={setEpisodeDraft}
        plannerTasks={plannerTasks}
        taskCatalog={taskCatalog}
        internalUsers={internalUsers}
        coordinatorUsers={coordinatorUsers}
        externalUsers={externalUsers}
        medicalForms={medicalForms}
        onClose={() => setPlannerOpen(false)}
        onAddCatalogTask={addCatalogTask}
        onPatchTask={patchPlannerTask}
        onToggleTaskForm={toggleTaskForm}
        onRemoveTask={removePlannerTask}
        onSave={() => void savePlanner()}
      />

      <Dialog
        open={episodeEditorOpen}
        title={tc('Edit Episode')}
        onClose={() => setEpisodeEditorOpen(false)}
        footer={
          <>
            <button type="button" className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 hover:bg-slate-50" onClick={() => setEpisodeEditorOpen(false)} disabled={busy}>
              {tc('Cancel')}
            </button>
            <button type="button" className="rounded-lg bg-ink px-3 py-2 text-sm text-white disabled:opacity-60" onClick={() => void saveEpisodeDetails()} disabled={busy || !episodeDraft.type.trim() || !episodeDraft.reason.trim()}>
              {tc('Save Episode')}
            </button>
          </>
        }
      >
        <EpisodeDetailsForm draft={episodeDraft} onChange={setEpisodeDraft} coordinatorUsers={coordinatorUsers} />
      </Dialog>

      <TaskEditorDialog
        open={Boolean(selectedTaskForEdit && taskEditorDraft)}
        busy={busy}
        task={selectedTaskForEdit}
        draft={taskEditorDraft}
        onChange={setTaskEditorDraft}
        onClose={closeTaskEditor}
        onSave={() => void saveTaskDetails()}
        onCancelTask={
          selectedTaskForEdit
            ? () => void (async () => {
                await cancelTask(selectedTaskForEdit);
                closeTaskEditor();
              })()
            : undefined
        }
        assigneeLabel={selectedTaskForEdit ? assigneeLabel(selectedTaskForEdit) : tc('Unassigned')}
      />
    </div>
  );
}

function PlannerDialog({
  open,
  mode,
  busy,
  episodeDraft,
  setEpisodeDraft,
  plannerTasks,
  taskCatalog,
  internalUsers,
  coordinatorUsers,
  externalUsers,
  medicalForms,
  onClose,
  onAddCatalogTask,
  onPatchTask,
  onToggleTaskForm,
  onRemoveTask,
  onSave,
}: {
  open: boolean;
  mode: 'new' | 'append';
  busy: boolean;
  episodeDraft: EpisodeDraft;
  setEpisodeDraft: (value: EpisodeDraft | ((prev: EpisodeDraft) => EpisodeDraft)) => void;
  plannerTasks: PlannerTaskDraft[];
  taskCatalog: CoordinationTaskCatalogEntry[];
  internalUsers: CoordinationPlanningInternalUser[];
  coordinatorUsers: CoordinationPlanningInternalUser[];
  externalUsers: CoordinationExternalUser[];
  medicalForms: CoordinationPlanningMedicalForm[];
  onClose: () => void;
  onAddCatalogTask: (template?: CoordinationTaskCatalogEntry) => void;
  onPatchTask: (taskId: string, patch: Partial<PlannerTaskDraft>) => void;
  onToggleTaskForm: (taskId: string, formId: string) => void;
  onRemoveTask: (taskId: string) => void;
  onSave: () => void;
}) {
  const [taskDialogOpen, setTaskDialogOpen] = useState(false);
  const { language, tr } = useI18n();
  const tc = createCoordinationTranslator(language, tr);

  useEffect(() => {
    if (!open) return;
    setTaskDialogOpen(false);
  }, [open]);

  if (!open) return null;

  return (
    <Dialog
      open={open}
      title={mode === 'new' ? tc('New Care Episode') : tc('Add Episode Tasks')}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 hover:bg-slate-50" onClick={onClose} disabled={busy}>{tc('Cancel')}</button>
          <button type="button" className="rounded-lg bg-ink px-3 py-2 text-sm text-white disabled:opacity-60" onClick={onSave} disabled={busy}>
            {mode === 'new' ? tc('Create Care Plan') : tc('Add Tasks')}
          </button>
        </>
      }
    >
      {mode === 'new' ? (
        <ModalFormSection title={tc('Episode Details')} subtitle={tc('Set the episode type, coordinator, risk, and timing using the same workflow style as appointment details.')}>
          <EpisodeDetailsForm draft={episodeDraft} onChange={setEpisodeDraft} coordinatorUsers={coordinatorUsers} />
        </ModalFormSection>
      ) : null}

      <ModalFormSection title={tc('Care Plan Tasks')} subtitle={tc('Manage care-plan rows here, and add new tasks from the table toolbar.')}>
        <div className="space-y-4">
          <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
            <div className="flex items-center justify-between gap-3 border-b border-slate-200 px-4 py-3">
              <div className="text-sm text-slate-500">
                {plannerTasks.length
                  ? language === 'it'
                    ? `${plannerTasks.length} attività nel piano`
                    : `${plannerTasks.length} task${plannerTasks.length === 1 ? '' : 's'} in plan`
                  : tc('No tasks added yet')}
              </div>
              <button
                type="button"
                className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700 hover:bg-white"
                onClick={() => setTaskDialogOpen(true)}
              >
                <Plus size={14} /> {tc('Add New Task')}
              </button>
            </div>

            <TaskPlannerTable
              tasks={plannerTasks}
              internalUsers={internalUsers}
              externalUsers={externalUsers}
              onPatch={onPatchTask}
              onRemove={onRemoveTask}
            />
          </div>

          <AddPlannerTaskDialog
            open={taskDialogOpen}
            taskCatalog={taskCatalog}
            onClose={() => setTaskDialogOpen(false)}
            onAddTask={(template) => {
              onAddCatalogTask(template);
              setTaskDialogOpen(false);
            }}
          />
        </div>
      </ModalFormSection>
    </Dialog>
  );
}

function EpisodeDetailsForm({
  draft,
  onChange,
  coordinatorUsers,
}: {
  draft: EpisodeDraft;
  onChange: (value: EpisodeDraft | ((prev: EpisodeDraft) => EpisodeDraft)) => void;
  coordinatorUsers: CoordinationPlanningInternalUser[];
}) {
  const { language, tr } = useI18n();
  const tc = createCoordinationTranslator(language, tr);
  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
      <div className="space-y-1">
        <FieldLabel icon={<ClipboardList size={14} />} label={tc('Episode Type')} />
        <input
          className={plannerFieldClass}
          placeholder={tc('Episode type')}
          value={draft.type}
          onChange={(event) => onChange((prev) => ({ ...prev, type: event.target.value }))}
        />
      </div>

      <div className="space-y-1">
        <FieldLabel icon={<span className={`h-2.5 w-2.5 rounded-full ${draft.riskLevel ? statusDotClass(draft.riskLevel) : 'bg-slate-300'}`} />} label={tc('Risk Level')} />
        <select className={plannerFieldClass} value={draft.riskLevel} onChange={(event) => onChange((prev) => ({ ...prev, riskLevel: event.target.value as EpisodeDraft['riskLevel'] }))}>
          <option value="">{tc('Select risk level')}</option>
          {riskLevels.map((item) => (
            <option key={item} value={item}>
              {tc(formatEnumLabel(item))}
            </option>
          ))}
        </select>
      </div>

      <div className="space-y-1">
        <FieldLabel icon={<UserPlus size={14} />} label={tc('Coordinator')} />
        <InternalUserPicker
          users={coordinatorUsers}
          value={draft.coordinatorId}
          onChange={(coordinatorId) => onChange((prev) => ({ ...prev, coordinatorId }))}
          emptyTitle={tc('Select coordinator')}
          emptyDescription={tc('Search providers by name, type, or email')}
          emptyBadge="CO"
        />
      </div>

      <div className="space-y-1">
        <FieldLabel icon={<span className={`h-2.5 w-2.5 rounded-full ${draft.status ? statusDotClass(draft.status) : 'bg-slate-300'}`} />} label={tc('Status')} />
        <select className={plannerFieldClass} value={draft.status} onChange={(event) => onChange((prev) => ({ ...prev, status: event.target.value as EpisodeDraft['status'] }))}>
          <option value="">{tc('Select status')}</option>
          {episodeStatusOptions.map((item) => (
            <option key={item} value={item}>
              {tc(formatEnumLabel(item))}
            </option>
          ))}
        </select>
      </div>

      <div className="space-y-1">
        <FieldLabel icon={<CalendarDays size={14} />} label={tc('Expected End Date')} />
        <input
          type="date"
          className={plannerFieldClass}
          value={draft.expectedEndDate}
          onChange={(event) => onChange((prev) => ({ ...prev, expectedEndDate: event.target.value }))}
        />
      </div>

      <div className="space-y-1 md:col-span-2">
        <FieldLabel icon={<MapPin size={14} />} label={tc('Title / Reason')} />
        <input
          className={plannerFieldClass}
          placeholder={tc('Clinical reason or episode notes')}
          value={draft.reason}
          onChange={(event) => onChange((prev) => ({ ...prev, reason: event.target.value }))}
        />
      </div>
    </div>
  );
}

function TaskEditorDialog({
  open,
  busy,
  task,
  draft,
  onChange,
  onClose,
  onSave,
  onCancelTask,
  assigneeLabel,
}: {
  open: boolean;
  busy: boolean;
  task: CoordinationTask | null;
  draft: TaskEditorDraft | null;
  onChange: (value: TaskEditorDraft | ((prev: TaskEditorDraft) => TaskEditorDraft)) => void;
  onClose: () => void;
  onSave: () => void;
  onCancelTask?: () => void;
  assigneeLabel: string;
}) {
  const { language, tr } = useI18n();
  const tc = createCoordinationTranslator(language, tr);
  if (!open || !task || !draft) return null;

  return (
    <Dialog
      open={open}
      title={tc('Edit Task')}
      onClose={onClose}
      footer={
        <>
          {task.status !== 'CANCELLED' ? (
            <button
              type="button"
              className="mr-auto rounded-lg border border-rose-200 bg-white px-3 py-2 text-sm text-rose-700 hover:bg-rose-50 disabled:opacity-60"
              onClick={onCancelTask}
              disabled={busy}
            >
              {tc('Cancel Task')}
            </button>
          ) : null}
          <button type="button" className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 hover:bg-slate-50" onClick={onClose} disabled={busy}>
            {tc('Close')}
          </button>
          <button type="button" className="rounded-lg bg-ink px-3 py-2 text-sm text-white disabled:opacity-60" onClick={onSave} disabled={busy || !draft.title.trim()}>
            {tc('Save Task')}
          </button>
        </>
      }
    >
      <ModalFormSection title={tc('Task Summary')} subtitle={tc('Review assignment and automation details for this care plan row.')}>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <InfoRows
            rows={[
              [tc('Assignee'), assigneeLabel],
              [tc('Recurrence'), translateCoordinationRecurrence(recurrenceLabel(task.metadata), tc)],
              [tc('Medical Forms'), formNames(task.metadata)],
              [tc('Send Rule'), translateCoordinationAutomation(automationLabel(task.metadata), tc)],
            ]}
          />
          <InfoRows
            rows={[
              [tc('Category'), task.metadata?.template?.category || tc('Care task')],
              [tc('Template Code'), task.metadata?.template?.code || '-'],
              [tc('Current Due'), formatCoordinationDate(task.dueAt)],
              [tc('Current Status'), tc(formatEnumLabel(task.status))],
            ]}
          />
        </div>
      </ModalFormSection>

      <ModalFormSection title={tc('Editable Fields')} subtitle={tc('Update task details here instead of editing directly in the table.')}>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div className="space-y-1 md:col-span-2">
            <FieldLabel icon={<ClipboardList size={14} />} label={tc('Task Title')} />
            <input
              className={plannerFieldClass}
              value={draft.title}
              onChange={(event) => onChange((prev) => ({ ...prev, title: event.target.value }))}
            />
          </div>

          <div className="space-y-1">
            <FieldLabel icon={<span className={`h-2.5 w-2.5 rounded-full ${statusDotClass(draft.status)}`} />} label={tc('Status')} />
            <select
              className={plannerFieldClass}
              value={draft.status}
              onChange={(event) => onChange((prev) => ({ ...prev, status: event.target.value as CoordinationTask['status'] }))}
            >
              {taskStatusOptions.map((status) => (
                <option key={status} value={status}>
                  {tc(formatEnumLabel(status))}
                </option>
              ))}
            </select>
          </div>

          <div className="space-y-1">
            <FieldLabel icon={<Clock3 size={14} />} label={tc('Next Due')} />
            <input
              type="datetime-local"
              className={plannerFieldClass}
              value={draft.dueAt}
              onChange={(event) => onChange((prev) => ({ ...prev, dueAt: event.target.value }))}
            />
          </div>

          <div className="space-y-1">
            <FieldLabel icon={<span className="h-2.5 w-2.5 rounded-full bg-slate-400" />} label={tc('Priority')} />
            <input
              className={plannerFieldClass}
              placeholder={tc('Priority')}
              value={draft.priority}
              onChange={(event) => onChange((prev) => ({ ...prev, priority: event.target.value }))}
            />
          </div>

          <div className="space-y-1 md:col-span-2">
            <FieldLabel icon={<MapPin size={14} />} label={tc('Instructions')} />
            <textarea
              className={`${plannerFieldClass} min-h-[120px] resize-y`}
              placeholder={tc('Task instructions')}
              value={draft.description}
              onChange={(event) => onChange((prev) => ({ ...prev, description: event.target.value }))}
            />
          </div>
        </div>
      </ModalFormSection>
    </Dialog>
  );
}

function FieldLabel({ icon, label }: { icon: ReactNode; label: string }) {
  return (
    <label className="flex items-center gap-2 text-xs font-semibold text-slate-600">
      <span className="shrink-0 text-slate-500">{icon}</span>
      {label}
    </label>
  );
}

function InternalUserPicker({
  users,
  value,
  onChange,
  emptyTitle = 'Select internal assignee',
  emptyDescription = 'Search team members by name, role, or email',
  emptyBadge = 'IA',
}: {
  users: CoordinationPlanningInternalUser[];
  value: string;
  onChange: (value: string) => void;
  emptyTitle?: string;
  emptyDescription?: string;
  emptyBadge?: string;
}) {
  const { language, tr } = useI18n();
  const tc = createCoordinationTranslator(language, tr);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const rootRef = useRef<HTMLDivElement | null>(null);
  const selectedUser = users.find((user) => user.id === value) || null;
  const filteredUsers = users.filter((user) => {
    const search = query.trim().toLowerCase();
    if (!search) return true;
    return [user.name, user.role, user.title || '', user.email || '', user.phone || ''].some((field) =>
      field.toLowerCase().includes(search),
    );
  });

  useEffect(() => {
    if (!open) return;
    const handlePointerDown = (event: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [open]);

  useEffect(() => {
    if (!open) setQuery('');
  }, [open]);

  const metaLine = selectedUser
    ? [selectedUser.role, selectedUser.title || null, selectedUser.email || null].filter(Boolean).join(' • ')
    : '';

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        className={`flex w-full items-center gap-3 rounded border px-3 py-2 text-left transition-colors ${
          open ? 'border-lime-400 ring-2 ring-lime-100' : 'border-slate-200 hover:border-slate-300'
        }`}
        onClick={() => setOpen((prev) => !prev)}
      >
        {selectedUser ? (
          <>
            <InitialAvatar name={selectedUser.name} src={selectedUser.avatarUrl} alt={selectedUser.name} className="h-10 w-10" />
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-medium text-slate-800">{selectedUser.name}</div>
              <div className="truncate text-xs text-slate-500">{metaLine}</div>
            </div>
          </>
        ) : (
          <>
            <span className="flex h-10 w-10 items-center justify-center rounded-full border border-dashed border-slate-300 bg-slate-50 text-xs font-semibold text-slate-500">
              {emptyBadge}
            </span>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium text-slate-700">{emptyTitle}</div>
              <div className="text-xs text-slate-500">{emptyDescription}</div>
            </div>
          </>
        )}
        <ChevronDown size={16} className={`shrink-0 text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open ? (
        <div className="absolute left-0 right-0 top-full z-30 mt-2 rounded-2xl border border-slate-200 bg-white p-3 shadow-lg">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
            <input
              autoFocus
              className="w-full rounded-xl border border-slate-300 px-3 py-2.5 pl-9 text-sm outline-none focus:border-lime-400 focus:ring-2 focus:ring-lime-100"
              placeholder={tc('Search by name, role, or email...')}
              value={query}
              onChange={(event) => setQuery(event.target.value)}
            />
          </div>
          <div className="mt-2 max-h-[280px] overflow-y-auto rounded-2xl border border-slate-100">
            {filteredUsers.length ? (
              filteredUsers.map((user) => {
                const detail = [user.role, user.title || null].filter(Boolean).join(' • ');
                const rightMeta = [user.email || null, user.phone || null].filter(Boolean).join(' • ');
                const active = user.id === value;
                return (
                  <button
                    key={user.id}
                    type="button"
                    className={`flex w-full items-center gap-3 border-b border-slate-100 px-3 py-3 text-left transition-colors last:border-0 ${
                      active ? 'bg-lime-50' : 'hover:bg-slate-50'
                    }`}
                    onMouseDown={(event) => {
                      event.preventDefault();
                      onChange(user.id);
                      setOpen(false);
                    }}
                  >
                    <InitialAvatar name={user.name} src={user.avatarUrl} alt={user.name} className="h-10 w-10" />
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-medium text-slate-800">{user.name}</div>
                      <div className="truncate text-xs text-slate-500">{[detail, rightMeta].filter(Boolean).join(' • ')}</div>
                    </div>
                  </button>
                );
              })
            ) : (
              <div className="p-3 text-sm text-slate-500">{tc('No internal users found.')}</div>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function TaskPlannerTable({
  tasks,
  internalUsers,
  externalUsers,
  onPatch,
  onRemove,
}: {
  tasks: PlannerTaskDraft[];
  internalUsers: CoordinationPlanningInternalUser[];
  externalUsers: CoordinationExternalUser[];
  onPatch: (taskId: string, patch: Partial<PlannerTaskDraft>) => void;
  onRemove: (taskId: string) => void;
}) {
  const { language, tr } = useI18n();
  const tc = createCoordinationTranslator(language, tr);
  return (
    <div className="overflow-x-auto overflow-y-visible">
      <table className="w-full table-fixed text-sm">
        <colgroup>
          <col className="w-[21%]" />
          <col className="w-[21%]" />
          <col className="w-[20%]" />
          <col className="w-[16%]" />
          <col className="w-[17%]" />
          <col className="w-[72px]" />
        </colgroup>
        <thead className="bg-white text-left text-[12px] font-semibold uppercase tracking-wide text-slate-500">
          <tr>
            <th className="px-3 py-3 font-medium">{tc('Task')}</th>
            <th className="px-3 py-3 font-medium">{tc('Category')}</th>
            <th className="px-3 py-3 font-medium">{tc('Assignment')}</th>
            <th className="px-3 py-3 font-medium">{tc('Due Date')}</th>
            <th className="px-3 py-3 font-medium">{tc('Instructions')}</th>
            <th className="px-2 py-3 font-medium text-right">{tc('Actions')}</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-200 bg-white align-middle">
          {tasks.length ? (
            tasks.map((task) => {
              const assigneeValue =
                task.assigneeType === 'internal' && task.internalUserId
                  ? `internal:${task.internalUserId}`
                  : task.assigneeType === 'external' && task.externalUserId
                    ? `external:${task.externalUserId}`
                    : 'unassigned';

              return (
                <tr key={task.id}>
                  <td className="px-3 py-3">
                    <div className="min-w-0">
                      <input
                        className={`${plannerFieldClass} font-medium text-slate-900`}
                        placeholder={tc('Task title')}
                        value={task.title}
                        onChange={(event) => onPatch(task.id, { title: event.target.value })}
                      />
                    </div>
                  </td>
                  <td className="px-3 py-3">
                    <div className="flex min-w-0 gap-2">
                      <input
                        className={`${plannerFieldClass} min-w-0 flex-1`}
                        placeholder={task.templateCode ? `${tc('Category')} (${task.templateCode})` : tc('Category')}
                        value={task.category}
                        onChange={(event) => onPatch(task.id, { category: event.target.value })}
                      />
                      <input
                        className={`${plannerFieldClass} w-[96px] shrink-0`}
                        placeholder={tc('Priority')}
                        value={task.priority}
                        onChange={(event) => onPatch(task.id, { priority: event.target.value })}
                      />
                    </div>
                  </td>
                  <td className="px-3 py-3">
                    <div className="min-w-0">
                      <select
                        className={plannerFieldClass}
                        value={assigneeValue}
                        onChange={(event) => {
                          const value = event.target.value;
                          if (value === 'unassigned') {
                            onPatch(task.id, { assigneeType: 'unassigned', internalUserId: '', externalUserId: '' });
                            return;
                          }
                          if (value.startsWith('internal:')) {
                            onPatch(task.id, {
                              assigneeType: 'internal',
                              internalUserId: value.slice('internal:'.length),
                              externalUserId: '',
                            });
                            return;
                          }
                          if (value.startsWith('external:')) {
                            onPatch(task.id, {
                              assigneeType: 'external',
                              externalUserId: value.slice('external:'.length),
                              internalUserId: '',
                            });
                          }
                        }}
                      >
                        <option value="unassigned">{tc('Unassigned')}</option>
                        {internalUsers.length ? (
                          <optgroup label={tc('Internal')}>
                            {internalUsers.map((user) => (
                              <option key={user.id} value={`internal:${user.id}`}>
                                {user.name}
                              </option>
                            ))}
                          </optgroup>
                        ) : null}
                        {externalUsers.length ? (
                          <optgroup label={tc('External')}>
                            {externalUsers.map((user) => (
                              <option key={user.id} value={`external:${user.id}`}>
                                {user.name}
                                {user.company ? ` - ${user.company}` : ''}
                              </option>
                            ))}
                          </optgroup>
                        ) : null}
                      </select>
                    </div>
                  </td>
                  <td className="px-3 py-3">
                    <div className="min-w-0">
                      <input
                        type="datetime-local"
                        className={plannerFieldClass}
                        value={task.dueAt}
                        onChange={(event) => onPatch(task.id, { dueAt: event.target.value })}
                      />
                    </div>
                  </td>
                  <td className="px-3 py-3">
                    <div className="min-w-0">
                      <input
                        className={plannerFieldClass}
                        placeholder={tc('Task instructions')}
                        value={task.description}
                        onChange={(event) => onPatch(task.id, { description: event.target.value })}
                      />
                    </div>
                  </td>
                  <td className="px-2 py-3">
                    <div className="flex justify-end">
                      <button
                        type="button"
                        className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-rose-200 text-rose-700 transition-colors hover:bg-rose-50"
                        onClick={() => onRemove(task.id)}
                        aria-label={tc('Remove task')}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })
          ) : (
            <tr>
              <td colSpan={6} className="px-4 py-10 text-center text-sm text-slate-500">
                {tc('Add at least one task to build the care plan.')}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function AddPlannerTaskDialog({
  open,
  taskCatalog,
  onClose,
  onAddTask,
}: {
  open: boolean;
  taskCatalog: CoordinationTaskCatalogEntry[];
  onClose: () => void;
  onAddTask: (template?: CoordinationTaskCatalogEntry) => void;
}) {
  const { language, tr } = useI18n();
  const tc = createCoordinationTranslator(language, tr);
  const [query, setQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const taskCategories = useMemo(
    () => ['All', ...Array.from(new Set(taskCatalog.map((item) => item.category))).sort((a, b) => a.localeCompare(b))],
    [taskCatalog],
  );
  const filteredCatalog = useMemo(() => {
    const search = query.trim().toLowerCase();
    return taskCatalog.filter((item) => {
      const matchesCategory = selectedCategory === 'All' || item.category === selectedCategory;
      const matchesSearch =
        !search ||
        [item.title, item.category, item.description, item.code]
          .filter(Boolean)
          .some((field) => field.toLowerCase().includes(search));
      return matchesCategory && matchesSearch;
    });
  }, [query, selectedCategory, taskCatalog]);

  useEffect(() => {
    if (!open) return;
    setQuery('');
    setSelectedCategory('All');
  }, [open]);

  if (!open) return null;

  return (
    <Dialog
      open={open}
      title={tc('Add New Task')}
      onClose={onClose}
      footer={
        <button type="button" className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 hover:bg-slate-50" onClick={onClose}>
          {tc('Close')}
        </button>
      }
    >
      <div className="space-y-4">
        <div className="flex flex-col gap-3 xl:flex-row xl:items-center">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
            <input
              autoFocus
              className={`${plannerFieldClass} pl-9`}
              placeholder={tc('Search task library')}
              value={query}
              onChange={(event) => setQuery(event.target.value)}
            />
          </div>
          <button
            type="button"
            className="inline-flex items-center justify-center gap-2 rounded-lg border border-dashed border-slate-300 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
            onClick={() => onAddTask(undefined)}
          >
            <Plus size={14} /> {tc('Add custom task')}
          </button>
        </div>

        <div className="flex flex-wrap gap-2">
          {taskCategories.map((category) => {
            const active = category === selectedCategory;
            return (
              <button
                key={category}
                type="button"
                className={`rounded-full border px-3 py-1.5 text-sm transition-colors ${
                  active
                    ? 'border-slate-900 bg-slate-900 text-white'
                    : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
                }`}
                onClick={() => setSelectedCategory(category)}
              >
                {category === 'All' ? tc('All') : category}
              </button>
            );
          })}
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
          <div className="max-h-[420px] overflow-y-auto">
            {filteredCatalog.length ? (
              filteredCatalog.map((entry) => (
                <button
                  key={entry.code}
                  type="button"
                  className="block w-full border-b border-slate-100 px-4 py-4 text-left transition-colors last:border-b-0 hover:bg-slate-50"
                  onClick={() => onAddTask(entry)}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-slate-800">{entry.title}</div>
                      <div className="mt-1 text-xs text-slate-500">{entry.category}</div>
                      <div className="mt-2 text-xs text-slate-500">{entry.description}</div>
                    </div>
                    <span className="inline-flex shrink-0 items-center rounded-full border border-slate-200 px-3 py-1 text-xs text-slate-700">
                      {tc('Add')}
                    </span>
                  </div>
                </button>
              ))
            ) : (
              <div className="px-4 py-10 text-center text-sm text-slate-500">{tc('No tasks match this search.')}</div>
            )}
          </div>
        </div>
      </div>
    </Dialog>
  );
}

function ModalFormSection({ title, subtitle, children }: { title: string; subtitle: string; children: ReactNode }) {
  return (
    <section className="rounded-2xl bg-white p-2 space-y-4">
      <div>
        <h4 className="font-semibold text-slate-800">{title}</h4>
        <p className="text-xs text-slate-500">{subtitle}</p>
      </div>
      {children}
    </section>
  );
}

function MetricCard({ label, value, helper, icon }: { label: string; value: string; helper: string; icon: ReactNode }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4">
      <div className="flex items-center justify-between text-slate-500">
        <span className="text-xs uppercase tracking-wide">{label}</span>
        {icon}
      </div>
      <div className="mt-2 text-2xl font-semibold text-slate-900">{value}</div>
      <div className="mt-1 text-sm text-slate-500">{helper}</div>
    </div>
  );
}

function SignalCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-3">
      <div className="text-xs uppercase tracking-wide text-slate-500">{label}</div>
      <div className="mt-1 text-lg font-semibold text-slate-900">{value}</div>
    </div>
  );
}

function InfoRows({ rows }: { rows: Array<[string, ReactNode]> }) {
  return (
    <div className="space-y-2 text-sm">
      {rows.map(([label, value]) => (
        <div key={label} className="flex items-start justify-between gap-4 border-b border-slate-100 pb-2 last:border-b-0 last:pb-0">
          <span className="text-slate-500">{label}</span>
          <span className="text-right text-slate-800">{value}</span>
        </div>
      ))}
    </div>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 space-y-3">
      <div className="text-sm font-semibold text-slate-800">{title}</div>
      {children}
    </div>
  );
}

function Dialog({
  open,
  title,
  onClose,
  footer,
  children,
}: {
  open: boolean;
  title: string;
  onClose: () => void;
  footer?: ReactNode;
  children: ReactNode;
}) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-40 flex items-start justify-center bg-slate-950/40 p-4" onClick={onClose}>
      <div className="mt-6 max-h-[calc(100vh-3rem)] w-full max-w-7xl overflow-auto rounded-3xl border border-slate-200 bg-slate-50 shadow-2xl" onClick={(event) => event.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-slate-200 bg-white px-6 py-5">
          <h5 className="text-2xl font-semibold text-slate-900">{title}</h5>
          <button type="button" className="rounded-xl p-2 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600" onClick={onClose}>
            <X size={28} />
          </button>
        </div>
        <div className="space-y-5 p-6">{children}</div>
        {footer ? <div className="flex items-center justify-end gap-3 border-t border-slate-200 bg-white px-6 py-5">{footer}</div> : null}
      </div>
    </div>
  );
}
